// Desfazer/refazer da aba Final — o padrão do VideoProjectEditor (past/present/future,
// teto de 50 passos) com gestos: arrastar um chip substitui o presente a cada quadro
// e empilha UM passo no fim, com o estado de antes do gesto.
import { useCallback, useRef, useState } from 'react';

interface History<T> { past: T[]; present: T; future: T[] }
const LIMIT = 50;

export function useHistory<T>(initial: T) {
  const [h, setH] = useState<History<T>>({ past: [], present: initial, future: [] });
  const gestureBefore = useRef<T | null>(null);
  const [generation, setGeneration] = useState(0);
  const bump = () => setGeneration(g => g + 1);

  const commit = useCallback((next: T | ((cur: T) => T)) => {
    setH(cur => {
      const value = typeof next === 'function' ? (next as (c: T) => T)(cur.present) : next;
      if (value === cur.present) return cur;
      return { past: [...cur.past.slice(-(LIMIT - 1)), cur.present], present: value, future: [] };
    });
    bump();
  }, []);

  const presentRef = useRef(h.present);
  presentRef.current = h.present;
  const beginGesture = useCallback(() => {
    gestureBefore.current = presentRef.current;
  }, []);

  const gesture = useCallback((next: (cur: T) => T) => {
    setH(cur => ({ ...cur, present: next(cur.present) }));
    bump();
  }, []);

  const endGesture = useCallback(() => {
    const before = gestureBefore.current;
    gestureBefore.current = null;
    if (before == null) return;
    setH(cur => (cur.present === before ? cur : { past: [...cur.past.slice(-(LIMIT - 1)), before], present: cur.present, future: [] }));
  }, []);

  const undo = useCallback(() => {
    setH(cur => {
      if (!cur.past.length) return cur;
      return { past: cur.past.slice(0, -1), present: cur.past[cur.past.length - 1], future: [cur.present, ...cur.future] };
    });
    bump();
  }, []);

  const redo = useCallback(() => {
    setH(cur => {
      if (!cur.future.length) return cur;
      return { past: [...cur.past, cur.present], present: cur.future[0], future: cur.future.slice(1) };
    });
    bump();
  }, []);

  const reset = useCallback((value: T) => {
    setH({ past: [], present: value, future: [] });
    gestureBefore.current = null;
  }, []);

  return {
    present: h.present,
    canUndo: h.past.length > 0,
    canRedo: h.future.length > 0,
    generation,
    commit, beginGesture, gesture, endGesture, undo, redo, reset,
  };
}
